/**
 * Fur & Haven behaviors shared by the fh-* sections and blocks. Loaded once as a module from
 * layout/theme.liquid. Every behavior is progressive: the markup works (links, forms, native
 * <details>/<dialog>) before this file runs.
 *
 * - [data-fh-reveal]        fade-up on first view (skipped with reduced motion and in the theme editor)
 * - <fh-header>             scrolled state and the mobile menu dialog
 * - <fh-dialog-trigger>     opens a <dialog> by id (mobile filters, menus)
 * - <fh-compare>            before/after comparison driven by a native range input
 * - <fh-rail>               previous/next buttons for horizontal rails
 * - <fh-size-shortcuts>     quick-pick buttons for featured option values in the product form rows
 * - <fh-recommendations>    loads Shopify product recommendations into sections/fh-product-grid.liquid
 * - [data-fh-scroll-to-buy] scrolls to the product form
 * - form[data-fh-autosubmit] submits filter/sort forms on change
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ Reveal */

function initReveal(root = document) {
  const targets = root.querySelectorAll('[data-fh-reveal]:not(.is-revealed)');
  if (!targets.length) return;
  if (reduceMotion.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
    targets.forEach((element) => element.classList.add('is-revealed'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  targets.forEach((element) => observer.observe(element));
}

/* ------------------------------------------------------------------ Header */

class FhHeader extends HTMLElement {
  #ticking = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('toggle', this.#onToggle, true);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    // Horizon scrolls inside .page-wrapper on desktop and the window on mobile: listen in the capture phase
    // so either scroller is heard.
    document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true });
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('toggle', this.#onToggle, true);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
  }

  /** Desktop dropdown panels: one open at a time. */
  #panels() {
    return /** @type {HTMLDetailsElement[]} */ (Array.from(this.querySelectorAll('.fh-nav__details')));
  }

  /** @param {Event} event */
  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('fh-nav__details') || !details.open) return;
    for (const other of this.#panels()) if (other !== details) other.open = false;
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = /** @type {Node} */ (event.target);
    for (const details of this.#panels()) if (details.open && !details.contains(target)) details.open = false;
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    for (const details of this.#panels()) {
      if (!details.open) continue;
      details.open = false;
      details.querySelector('summary')?.focus();
    }
  };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#update();
      this.#ticking = false;
    });
  };

  #update() {
    const wrapper = document.querySelector('.page-wrapper');
    const offset = Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
    this.toggleAttribute('data-scrolled', offset > 8);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const opener = target.closest('[data-fh-menu-open]');
    const closer = target.closest('[data-fh-menu-close]');
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-fh-menu]'));
    if (!dialog) return;
    if (opener) {
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      dialog.addEventListener(
        'close',
        () => {
          opener.setAttribute('aria-expanded', 'false');
          document.documentElement.style.overflow = '';
        },
        { once: true }
      );
    } else if (closer || target === dialog) {
      dialog.close();
    }
  };
}

/* ------------------------------------------------------------------ Dialog trigger */

class FhDialogTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = () => {
    const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(this.dataset.dialog ?? ''));
    if (!dialog || dialog.open) return;
    if (!dialog.dataset.fhBound) {
      dialog.dataset.fhBound = 'true';
      dialog.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target === dialog || target.closest('[data-fh-dialog-close]')) dialog.close();
      });
    }
    dialog.showModal();
  };
}

/* ------------------------------------------------------------------ Before / after comparison */

class FhCompare extends HTMLElement {
  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="range"]'));
    this.input?.addEventListener('input', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#update);
  }

  #update = () => {
    const value = Number(this.input?.value ?? 50);
    this.style.setProperty('--fh-compare', `${value}%`);
  };
}

/* ------------------------------------------------------------------ Rail buttons */

class FhRail extends HTMLElement {
  connectedCallback() {
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-fh-rail-track]'));
    this.prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-fh-rail-prev]'));
    this.next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-fh-rail-next]'));
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.#go(-1));
    this.next?.addEventListener('click', () => this.#go(1));
    this.track.addEventListener('scroll', this.#update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.#update);
    this.resizeObserver.observe(this.track);
    this.#update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /** @param {number} direction */
  #go(direction) {
    if (!this.track) return;
    const item = /** @type {HTMLElement | null} */ (this.track.firstElementChild);
    const step = (item?.getBoundingClientRect().width ?? 300) + 16;
    this.track.scrollBy({ left: direction * step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  #update = () => {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    this.toggleAttribute('data-overflowing', max > 0);
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
  };
}

/* ------------------------------------------------------------------ Size shortcuts */

/**
 * Buttons for a product's featured option values (custom.featured_sizes). Clicking one sets that option
 * on the product form's option rows (snippets/offer-tiers.liquid): the single row, or every row of the
 * selected offer card that the shopper hasn't fixed by a set preset.
 */
class FhSizeShortcuts extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') ?? document;
    this.addEventListener('click', this.#onClick);
    this.section.addEventListener('change', this.#sync);
    this.section.addEventListener('bundle-offers:selection', this.#sync);
    requestAnimationFrame(this.#sync);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.section?.removeEventListener('change', this.#sync);
    this.section?.removeEventListener('bundle-offers:selection', this.#sync);
  }

  get #optionName() {
    return (this.dataset.option || 'Size').trim().toLowerCase();
  }

  /** Selects for the configured option in the rows that are currently shown. */
  #targets() {
    const offers = this.section?.querySelector('bundle-offers');
    if (!offers) return [];
    const panel = offers.querySelector('[data-bundle-variants]:not([hidden])');
    if (!panel) return [];
    const index = this.dataset.optionIndex;
    return /** @type {HTMLSelectElement[]} */ (
      Array.from(panel.querySelectorAll(`select[data-option-index="${index}"]`)).filter(
        (select) => !(/** @type {HTMLSelectElement} */ (select).disabled)
      )
    );
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-value]');
    if (!(button instanceof HTMLButtonElement)) return;
    const value = button.dataset.value ?? '';
    const selects = this.#targets();
    if (!selects.length) {
      // No option rows on this template: fall back to Horizon's variant picker inputs.
      const input = /** @type {HTMLInputElement | null} */ (
        this.section?.querySelector(`variant-picker input[type="radio"][value="${CSS.escape(value)}"]`)
      );
      input?.click();
      return;
    }
    for (const select of selects) {
      if (!Array.from(select.options).some((option) => option.value === value)) continue;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#sync();
  };

  #sync = () => {
    const targets = this.#targets();
    const current = targets[0]?.value;
    // A selected set fixes every size: the quick picks have nothing to change.
    const inactive = !!this.section?.querySelector('bundle-offers') && targets.length === 0;
    this.toggleAttribute('data-inactive', inactive);
    for (const button of /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('button[data-value]'))) {
      button.disabled = inactive;
      button.setAttribute('aria-pressed', String(!inactive && !!current && button.getAttribute('data-value') === current));
    }
  };
}

/* ------------------------------------------------------------------ Product recommendations */

/**
 * sections/fh-product-grid.liquid with Shopify recommendations: fetches the section rendered with
 * recommendations and swaps itself for it, or removes itself when Shopify returns none.
 */
class FhRecommendations extends HTMLElement {
  async connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('.fh-pgrid');
      if (!content) {
        this.closest('.shopify-section')?.setAttribute('hidden', '');
        this.remove();
        return;
      }
      this.replaceWith(content);
      initReveal(content);
    } catch {
      this.remove();
    }
  }
}

/* ------------------------------------------------------------------ Auto-submitting filter forms */

function initAutoSubmit(root = document) {
  for (const form of root.querySelectorAll('form[data-fh-autosubmit]')) {
    if (form.hasAttribute('data-fh-bound')) continue;
    form.setAttribute('data-fh-bound', '');
    let timer = 0;
    form.addEventListener('change', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.matches('[data-fh-no-autosubmit], [data-fh-no-autosubmit] *')) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => /** @type {HTMLFormElement} */ (form).requestSubmit(), 250);
    });
  }
}

/* ------------------------------------------------------------------ Scroll to the product form */

document.addEventListener('click', (event) => {
  const link = /** @type {HTMLElement} */ (event.target).closest?.('[data-fh-scroll-to-buy]');
  if (!link) return;
  const form = document.querySelector('bundle-offers, product-form-component, .product-details');
  if (!form) return;
  event.preventDefault();
  form.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
  const focusable = /** @type {HTMLElement | null} */ (form.querySelector('[role="radio"][tabindex="0"], select, button'));
  focusable?.focus({ preventScroll: true });
});

/* ------------------------------------------------------------------ Cart page quantities */

document.addEventListener('change', (event) => {
  const input = /** @type {HTMLElement} */ (event.target);
  if (!(input instanceof HTMLInputElement) || !input.matches('.fh-qty__input')) return;
  const form = /** @type {HTMLFormElement | null} */ (input.closest('form[data-fh-cart]'));
  const update = /** @type {HTMLButtonElement | null} */ (form?.querySelector('[data-fh-cart-update]') ?? null);
  if (form && update) form.requestSubmit(update);
});

/* ------------------------------------------------------------------ Boot */

const elements = {
  'fh-header': FhHeader,
  'fh-dialog-trigger': FhDialogTrigger,
  'fh-compare': FhCompare,
  'fh-rail': FhRail,
  'fh-size-shortcuts': FhSizeShortcuts,
  'fh-recommendations': FhRecommendations,
};

for (const [name, constructor] of Object.entries(elements)) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

initReveal();
initAutoSubmit();

// Re-run after the theme editor re-renders a section.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  initReveal(target);
  initAutoSubmit(target);
});
